/**
 * 无限画布 NodeGraphCanvas 各节点模型选项（单一数据源）
 * 与 src/views/NodeGraphCanvas.vue 中 llm / image / video / audio 下拉保持一致
 */

/** 画布视频节点 seedanceModel 为空时，后端默认接入点（见 FastMovieController SeedanceModelId） */
export const NGC_SEEDANCE_BACKEND_DEFAULT = 'doubao-seedance-2-0-fast-260128'

export const NGC_MODEL_GROUP = {
  LLM: '无限画布 · 文本 / 分镜',
  IMAGE: '无限画布 · 图片',
  VIDEO: '无限画布 · 视频',
  AUDIO: '无限画布 · 音频'
}

/** 文本节点 model、分镜/电商 deepseekModel（部分）；积分仅来自 GET /modelslist */
export const NGC_LLM_MODEL_OPTIONS = [
  { id: 'deepseek-v4-flash', label: 'DeepSeek V4 Flash' },
  { id: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro' },
  {
    id: 'doubao-seed-character-251128',
    label: '豆包 Seed Character（火山方舟）'
  }
]

/** 图片节点 seedreamModel、电商出图 */
export const NGC_IMAGE_MODEL_OPTIONS = [
  { id: 'doubao-seedream-5-0-260128', label: 'Seedream 5.0' },
  { id: 'gpt-image-2', label: 'GPT-Image-2（API易）' },
  { id: 'gpt-image-2-duomi', label: 'GPT-Image-2-d' },
  { id: 'gpt-image-2-all', label: 'GPT-Image-2-All（API易·官逆）' },
  { id: 'gpt-image-2-vip', label: 'GPT-Image-2-VIP（API易·可锁 size）' },
  { id: 'nano-banana-2', label: 'Nano Banana 2（API易）' }
]

/** 视频节点 seedanceModel；id 为空表示 Seedance 2.0 VIP（走后端默认端点） */
export const NGC_VIDEO_MODEL_OPTIONS = [
  { id: '', label: 'Seedance 2.0 VIP', backendDefault: NGC_SEEDANCE_BACKEND_DEFAULT },
  {
    id: NGC_SEEDANCE_BACKEND_DEFAULT,
    label: 'Seedance 2.0 Fast（端点 ID）'
  },
  /** 百炼 HappyHorse 1.1（文生/图生/参考；主条目按节点模式自动切 t2v/i2v/r2v） */
  { id: 'happyhorse-1.1-t2v', label: 'HappyHorse 1.1' },
  { id: 'happyhorse-1.1-i2v', label: 'HappyHorse 1.1 · 图生视频' },
  { id: 'happyhorse-1.1-r2v', label: 'HappyHorse 1.1 · 参考图生视频' },
  { id: 'happyhorse-1.0-t2v', label: 'HappyHorse 1.0' },
  { id: 'happyhorse-1.0-i2v', label: 'HappyHorse 1.0 · 图生视频' },
  { id: 'happyhorse-1.0-r2v', label: 'HappyHorse 1.0 · 参考图生视频' },
  { id: 'happyhorse-1.0-video-edit', label: 'HappyHorse 1.0 · 视频编辑' },
  { id: 'wan2.2-animate-move', label: '万相 · 动作模仿（Animate Move）' }
]

/** 音频节点 model */
export const NGC_AUDIO_MODEL_OPTIONS = [
  {
    id: 'volc-doubao-tts-2',
    label: '豆包·语音合成 2.0（OpenSpeech）'
  },
  {
    id: 'volc-doubao-icl-2',
    label: '豆包·声音复刻 2.0（上传参考音）'
  },
  {
    id: 'volc-doubao-tts-2-expressive',
    label: '豆包·合成 2.0 高表现（req model）'
  },
  // { id: 'minimax-speech-2.8-hd', label: '兼容：Minimax / yinpin 链路' }
]

const KIND_BY_GROUP = {
  [NGC_MODEL_GROUP.LLM]: 'text',
  [NGC_MODEL_GROUP.IMAGE]: 'image',
  [NGC_MODEL_GROUP.VIDEO]: 'video',
  [NGC_MODEL_GROUP.AUDIO]: 'audio'
}

function mapCanvasOption(m, group, nodeField) {
  const rawId = m && m.id != null ? String(m.id) : ''
  const value =
    rawId.trim() ||
    (m && m.backendDefault ? String(m.backendDefault).trim() : '')
  if (!value) return null
  const label = String((m && m.label) || value)
  const canvasId = rawId.trim()
  const suffix =
    canvasId === '' && m && m.backendDefault
      ? '（画布默认 · 端点 ID）'
      : ''
  return {
    value,
    label: label + suffix,
    name: String((m && m.label) || value).replace(/（画布默认 · 端点 ID）$/, ''),
    kind: KIND_BY_GROUP[group] || 'image',
    group,
    nodeField,
    canvasId
  }
}

/** 后台模型定价：按画布分组 + 扁平列表 */
export function buildAdminModelPresetGroups() {
  return [
    {
      key: 'llm',
      label: NGC_MODEL_GROUP.LLM,
      options: NGC_LLM_MODEL_OPTIONS.map(m =>
        mapCanvasOption(m, NGC_MODEL_GROUP.LLM, 'model / deepseekModel')
      ).filter(Boolean)
    },
    {
      key: 'image',
      label: NGC_MODEL_GROUP.IMAGE,
      options: NGC_IMAGE_MODEL_OPTIONS.map(m =>
        mapCanvasOption(m, NGC_MODEL_GROUP.IMAGE, 'seedreamModel')
      ).filter(Boolean)
    },
    {
      key: 'video',
      label: NGC_MODEL_GROUP.VIDEO,
      options: NGC_VIDEO_MODEL_OPTIONS.map(m =>
        mapCanvasOption(m, NGC_MODEL_GROUP.VIDEO, 'seedanceModel')
      ).filter(Boolean)
    },
    {
      key: 'audio',
      label: NGC_MODEL_GROUP.AUDIO,
      options: NGC_AUDIO_MODEL_OPTIONS.map(m =>
        mapCanvasOption(m, NGC_MODEL_GROUP.AUDIO, 'model')
      ).filter(Boolean)
    }
  ]
}

export function buildAdminModelPresetsFlat() {
  const seen = new Set()
  const out = []
  for (const g of buildAdminModelPresetGroups()) {
    for (const opt of g.options || []) {
      if (seen.has(opt.value)) continue
      seen.add(opt.value)
      out.push(opt)
    }
  }
  return out
}

export function findCanvasPresetByModelId(modelId) {
  const id = String(modelId || '').trim()
  if (!id) return null
  return buildAdminModelPresetsFlat().find(o => o.value === id) || null
}
